import { AREA, NET, PROTOCOL_VERSION, distanceOutside, parseRegionKey, regionBounds, regionKey, regionsNear, type ClientMessage, type Look, type ServerMessage, type Vec2 } from "@dyson/shared";
import { Connection } from "./Connection";
import type { Session } from "./session";

/**
 * Watching sockets are told where we are once we've moved this far (blocks)
 * since we last told them, at most every LOOK_MS. Standing still we say
 * nothing, so a region we only watch can stay quiet (each message wakes it,
 * and is billed).
 */
const LOOK_MOVE = 20;
const LOOK_MS = 250;

/**
 * Our sockets to the regions of the sphere (see plan.md, "Area borders", and
 * stream_plan.md, "Clients"): one to the region our character is in, which
 * we play through (the owner: the area we're over, or the segment of the
 * stream rock we're riding), and one to each other region within
 * NET.viewRadius of us (areas, and segments whose footprint is near), which
 * we only watch, so whatever is across a border is drawn seamlessly. When
 * our character crosses a border the owner changes: the old region says
 * "handoff", the new one "owned", and input goes there from then on.
 *
 * A sphere that's one area ("all") just has the one socket.
 */
export class AreaLinks {
  private readonly links = new Map<string, Connection>();
  private owner?: string;
  /** Handed off, waiting for the new region's "owned": input is held till then. */
  private moving = false;
  private lastLook = 0;
  private lookedFrom?: Vec2;
  private name = "";
  private look?: Look;

  /** A message from the region `from` (its key, or "all"). */
  onMessage: (m: ServerMessage, from: string) => void = () => {};
  /** The socket we play through closed: reconnect from scratch. */
  onOwnerClosed: (reason: string) => void = () => {};

  constructor(
    private readonly wsBase: string,
    private session: Session,
  ) {}

  /** Sockets now get this session's ticket (a renewed one, or Node's token once we have it). */
  setSession(session: Session): void {
    this.session = session;
  }

  get sphere() {
    return this.session.sphere;
  }

  /** Connect to our character's region and join there. */
  join(name: string, look: Look | undefined): void {
    this.name = name;
    this.look = look;
    this.closeAll();
    const key = this.session.region ?? "all";
    this.owner = key;
    this.moving = false;
    this.open(key, (c) => c.send({ t: "join", version: PROTOCOL_VERSION, name, ticket: this.session.ticket || undefined, look }));
  }

  get connected(): boolean {
    return !!this.owner && !this.moving && !!this.links.get(this.owner)?.connected;
  }

  /** The region we play in ("x,y", "s:k" or "all"). */
  get ownerKey(): string | undefined {
    return this.owner;
  }

  /** Send to the region we play in (dropped while moving between regions). */
  send(msg: ClientMessage): void {
    if (this.connected) this.links.get(this.owner!)!.send(msg);
  }

  /** Our character was handed to region `to`. Hold input until it says it has us; open a socket there if we hadn't one. */
  handedOff(to: string): void {
    // "owned" can beat "handoff" here (they come over different sockets).
    if (to === this.owner) return;
    this.moving = true;
    if (!this.links.has(to)) {
      // Not one we were watching (a teleport, or knocked out far from here): join there directly.
      this.open(to, (c) => c.send({ t: "join", version: PROTOCOL_VERSION, name: this.name, ticket: this.session.ticket || undefined, look: this.look }));
    }
  }

  /** Region `key` has our character now: play through it. */
  owned(key: string): void {
    this.owner = key;
    this.moving = false;
    this.session = { ...this.session, region: key === "all" ? null : key };
  }

  /**
   * We're at `at` (at rock clock `time`, s): watch every region within view
   * of it (and stop watching those well out of view), and tell the ones we
   * watch where we are.
   */
  update(at: Vec2, time: number): void {
    const sphere = this.session.sphere;
    if (!this.owner || this.owner === "all") return;
    const want = new Set(regionsNear(sphere, at, NET.viewRadius, time).map(regionKey));
    for (const key of want) {
      if (this.links.has(key)) continue;
      this.open(key, (c) => c.send({ t: "view", version: PROTOCOL_VERSION, ticket: this.session.ticket || undefined, x: at.x, y: at.y }));
    }
    for (const [key, c] of [...this.links]) {
      if (key === this.owner || want.has(key)) continue;
      const r = parseRegionKey(key);
      if (r && distanceOutside(regionBounds(sphere, r, time), at) < NET.viewRadius + AREA.dropHysteresis) continue;
      this.links.delete(key);
      c.close();
    }
    const now = performance.now();
    if (now - this.lastLook < LOOK_MS) return;
    if (this.lookedFrom && Math.hypot(at.x - this.lookedFrom.x, at.y - this.lookedFrom.y) < LOOK_MOVE) return;
    this.lastLook = now;
    this.lookedFrom = { ...at };
    for (const [key, c] of this.links) if (key !== this.owner) c.send({ t: "look", x: at.x, y: at.y });
  }

  /** The regions we have sockets to. */
  get areas(): string[] {
    return [...this.links.keys()];
  }

  closeAll(): void {
    for (const c of this.links.values()) c.close();
    this.links.clear();
    this.owner = undefined;
  }

  private open(key: string, hello: (c: Connection) => void): void {
    const c = new Connection(key === "all" ? `${this.wsBase}/ws` : `${this.wsBase}/ws/${key}`);
    this.links.set(key, c);
    c.onOpen = () => hello(c);
    c.onMessage = (m) => this.onMessage(m, key);
    c.onClose = (reason) => {
      if (this.links.get(key) !== c) return;
      this.links.delete(key);
      if (key === this.owner) this.onOwnerClosed(reason);
      // A watching socket that drops is opened again by the next update().
    };
    c.connect();
  }
}
